import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import { ShieldCheckIcon, TrashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';

interface PrivacySettingsData {
  store_prompts: boolean;
  store_improvements: boolean;
  store_context: boolean;
  allow_analytics: boolean;
  allow_anonymous_analytics: boolean;
  share_with_team: boolean;
  sync_history: boolean;
  sync_frequency: string;
  excluded_sources?: string[];
  auto_delete_days?: number;
}

interface DataExport {
  id: string;
  export_type: string;
  format: string;
  status: string;
  requested_at: string;
  completed_at?: string;
  download_url?: string;
  expires_at?: string;
}

export const PrivacySettings: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<PrivacySettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exports, setExports] = useState<DataExport[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    if (user) {
      loadPrivacySettings();
      loadDataExports();
    }
  }, [user]);

  const loadPrivacySettings = async () => {
    try {
      const response = await apiClient.get('/api/auth/privacy');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load privacy settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDataExports = async () => {
    try {
      const response = await apiClient.get('/api/auth/privacy/exports');
      setExports(response.data);
    } catch (error) {
      console.error('Failed to load data exports:', error);
    }
  };

  const updateSetting = async (key: keyof PrivacySettingsData, value: any) => {
    if (!settings) return;

    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    setSaving(true);

    try {
      await apiClient.put('/api/auth/privacy', { [key]: value });
    } catch (error) {
      console.error('Failed to update privacy setting:', error);
      // Revert on error
      setSettings(settings);
    } finally {
      setSaving(false);
    }
  };

  const requestDataExport = async (type: string = 'full', format: string = 'json') => {
    try {
      const response = await apiClient.post('/api/auth/privacy/export', {
        export_type: type,
        format: format
      });
      await loadDataExports();
      alert('Data export requested. You will receive a download link when ready.');
    } catch (error) {
      console.error('Failed to request data export:', error);
      alert('Failed to request data export.');
    }
  };

  const deleteAllData = async () => {
    if (deleteConfirmText !== 'DELETE_ALL_MY_DATA') {
      alert('Please type DELETE_ALL_MY_DATA to confirm');
      return;
    }

    try {
      await apiClient.delete(`/api/auth/privacy/data?confirmation=${encodeURIComponent(deleteConfirmText)}`);
      alert('All your data has been permanently deleted.');
      // Log out user
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to delete data:', error);
      alert('Failed to delete data.');
    }
  };

  if (loading) {
    return <div className="p-8">Loading privacy settings...</div>;
  }

  if (!settings) {
    return <div className="p-8">Failed to load privacy settings</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <ShieldCheckIcon className="h-6 w-6" />
          Privacy Settings
        </h2>
        <p className="text-gray-600 mt-2">
          Control how your data is collected, stored, and used
        </p>
      </div>

      {/* Data Collection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Data Collection</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium">Store Prompts</label>
              <p className="text-sm text-gray-500">Save your original prompts for history</p>
            </div>
            <Switch
              checked={settings.store_prompts}
              onChange={(value) => updateSetting('store_prompts', value)}
              disabled={saving}
              className={`${
                settings.store_prompts ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
            >
              <span className={`${
                settings.store_prompts ? 'translate-x-6' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
            </Switch>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium">Store Improvements</label>
              <p className="text-sm text-gray-500">Save improved versions of your prompts</p>
            </div>
            <Switch
              checked={settings.store_improvements}
              onChange={(value) => updateSetting('store_improvements', value)}
              disabled={saving || !settings.store_prompts}
              className={`${
                settings.store_improvements ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
            >
              <span className={`${
                settings.store_improvements ? 'translate-x-6' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
            </Switch>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium">Store Context</label>
              <p className="text-sm text-gray-500">Save context information with prompts</p>
            </div>
            <Switch
              checked={settings.store_context}
              onChange={(value) => updateSetting('store_context', value)}
              disabled={saving || !settings.store_prompts}
              className={`${
                settings.store_context ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
            >
              <span className={`${
                settings.store_context ? 'translate-x-6' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
            </Switch>
          </div>
        </div>
      </div>

      {/* Analytics */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Analytics</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium">Allow Analytics</label>
              <p className="text-sm text-gray-500">Share usage data to help improve the service</p>
            </div>
            <Switch
              checked={settings.allow_analytics}
              onChange={(value) => updateSetting('allow_analytics', value)}
              disabled={saving}
              className={`${
                settings.allow_analytics ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
            >
              <span className={`${
                settings.allow_analytics ? 'translate-x-6' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
            </Switch>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium">Anonymous Analytics</label>
              <p className="text-sm text-gray-500">Share anonymized usage patterns only</p>
            </div>
            <Switch
              checked={settings.allow_anonymous_analytics}
              onChange={(value) => updateSetting('allow_anonymous_analytics', value)}
              disabled={saving}
              className={`${
                settings.allow_anonymous_analytics ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
            >
              <span className={`${
                settings.allow_anonymous_analytics ? 'translate-x-6' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
            </Switch>
          </div>
        </div>
      </div>

      {/* Sync Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Sync Settings</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium">Sync History</label>
              <p className="text-sm text-gray-500">Sync prompt history across devices</p>
            </div>
            <Switch
              checked={settings.sync_history}
              onChange={(value) => updateSetting('sync_history', value)}
              disabled={saving || !settings.store_prompts}
              className={`${
                settings.sync_history ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
            >
              <span className={`${
                settings.sync_history ? 'translate-x-6' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
            </Switch>
          </div>

          <div>
            <label className="font-medium">Sync Frequency</label>
            <select
              value={settings.sync_frequency}
              onChange={(e) => updateSetting('sync_frequency', e.target.value)}
              disabled={saving || !settings.sync_history}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="manual">Manual</option>
              <option value="realtime">Real-time</option>
              <option value="daily">Daily</option>
            </select>
          </div>

          <div>
            <label className="font-medium">Auto-delete after days</label>
            <input
              type="number"
              value={settings.auto_delete_days || ''}
              onChange={(e) => updateSetting('auto_delete_days', e.target.value ? parseInt(e.target.value) : null)}
              placeholder="Never"
              min="1"
              max="365"
              disabled={saving}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Data Management</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">Export Your Data</h4>
            <div className="flex gap-2">
              <button
                onClick={() => requestDataExport('full', 'json')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Export All Data (JSON)
              </button>
              <button
                onClick={() => requestDataExport('prompts', 'csv')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Export Prompts (CSV)
              </button>
            </div>
          </div>

          {exports.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Recent Exports</h4>
              <div className="space-y-2">
                {exports.slice(0, 3).map((exp) => (
                  <div key={exp.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm">
                      {exp.export_type} ({exp.format}) - {exp.status}
                    </span>
                    {exp.download_url && (
                      <a
                        href={exp.download_url}
                        className="text-blue-600 hover:underline text-sm"
                        download
                      >
                        Download
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-t pt-4">
            <h4 className="font-medium mb-2 text-red-600">Delete All Data</h4>
            <p className="text-sm text-gray-500 mb-2">
              This will permanently delete all your data including your account. This action cannot be undone.
            </p>
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                <TrashIcon className="h-4 w-4" />
                Delete All My Data
              </button>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE_ALL_MY_DATA to confirm"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500"
                />
                <div className="flex gap-2">
                  <button
                    onClick={deleteAllData}
                    disabled={deleteConfirmText !== 'DELETE_ALL_MY_DATA'}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    Confirm Delete
                  </button>
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};